"use client"

import { useEffect, useMemo, useState } from "react"
import { BadgePercent, X } from "lucide-react"
import type { SiteSettings } from "@/lib/settings"

type PromoBannerSettings = SiteSettings['commerce']['promotionBanner']

export function PromoBanner({ banner }: { banner: PromoBannerSettings }) {
  const [dismissed, setDismissed] = useState(false)
  const storageKey = useMemo(
    () => `promo-banner-dismissed:${banner.code || 'default'}`,
    [banner.code]
  )

  useEffect(() => {
    setDismissed(window.localStorage.getItem(storageKey) === 'true')
  }, [storageKey])

  if (!banner.enabled || !banner.code || dismissed) return null

  function dismiss() {
    window.localStorage.setItem(storageKey, 'true')
    setDismissed(true)
  }

  return (
    <aside className="border-b bg-primary/10 px-4 py-3 text-primary">
      <div className="container mx-auto flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/20">
            <BadgePercent className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{banner.headline}</p>
            <p className="mt-0.5 text-sm leading-6 text-muted-foreground">
              {banner.description}{' '}
              <span className="inline-flex rounded-md border bg-background px-2 py-0.5 font-mono text-xs font-semibold tracking-wide text-foreground">
                {banner.code}
              </span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Dismiss promotion banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </aside>
  )
}
